import { useState } from "react";
import { Crown, Loader2, Trash2, UserPlus } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  useAddProjectMember,
  useProjectMembers,
  useRemoveProjectMember,
  useSearchUsers,
} from "@/hooks/use-projects";
import { useAuth } from "@/components/providers/auth-provider";

interface ProjectMembersDialogProps {
  projectId: string;
  open: boolean;
  onClose: () => void;
  /** Solo owner/admin pueden añadir y quitar miembros. */
  canManage: boolean;
}

export function ProjectMembersDialog({
  projectId,
  open,
  onClose,
  canManage,
}: ProjectMembersDialogProps) {
  const { user } = useAuth();
  const membersQuery = useProjectMembers(projectId);
  const addMember = useAddProjectMember(projectId);
  const removeMember = useRemoveProjectMember(projectId);

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"owner" | "member">("member");
  const searchQuery = useSearchUsers(email);

  const members = membersQuery.data ?? [];
  const suggestions = (searchQuery.data ?? []).filter(
    (u) =>
      u.email !== email && !members.some((m) => m.email === u.email)
  );

  function handleAdd() {
    const trimmed = email.trim();
    if (!trimmed || addMember.isPending) return;
    addMember.mutate(
      { email: trimmed, role },
      { onSuccess: () => setEmail("") }
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Miembros del proyecto"
      description="Los miembros pueden subir documentos y usar el chat."
    >
      <div className="space-y-4">
        {canManage && (
          <div className="space-y-2">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@empresa.com"
                  className="w-full px-3 py-2 rounded border border-line bg-surface text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/15"
                  aria-label="Email del nuevo miembro"
                />
                {suggestions.length > 0 && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-surface border border-line rounded-md shadow-card z-10 overflow-hidden">
                    {suggestions.slice(0, 5).map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => setEmail(u.email)}
                        className="block w-full text-left px-3 py-2 text-sm hover:bg-surface-100"
                      >
                        <span className="font-medium text-ink">
                          {u.nombre || u.email}
                        </span>
                        {u.nombre && (
                          <span className="text-content-muted text-xs ml-2">
                            {u.email}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as "owner" | "member")}
                className="px-2 py-2 rounded border border-line bg-surface text-sm focus:outline-none focus:border-brand"
                aria-label="Rol"
              >
                <option value="member">Miembro</option>
                <option value="owner">Propietario</option>
              </select>
              <Button
                onClick={handleAdd}
                disabled={!email.trim() || addMember.isPending}
              >
                {addMember.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <UserPlus className="w-4 h-4" />
                )}
                Añadir
              </Button>
            </div>
          </div>
        )}

        {membersQuery.isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="w-5 h-5 text-content-muted animate-spin" />
          </div>
        ) : members.length === 0 ? (
          <p className="text-sm text-content-muted text-center py-4">
            No hay miembros en este proyecto.
          </p>
        ) : (
          <ul className="divide-y divide-line/60 border border-line rounded-md">
            {members.map((member) => (
              <li
                key={member.user_id}
                className="flex items-center gap-3 px-3 py-2.5"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-ink truncate">
                    {member.nombre || member.email}
                    {member.email === user?.email && (
                      <span className="text-content-muted font-normal">
                        {" "}
                        (tú)
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-content-muted truncate">
                    {member.email}
                  </p>
                </div>
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-pill ${
                    member.role === "owner"
                      ? "bg-brand/10 text-brand-hover"
                      : "bg-surface-200 text-content-muted"
                  }`}
                >
                  {member.role === "owner" && <Crown className="w-3 h-3" />}
                  {member.role === "owner" ? "Propietario" : "Miembro"}
                </span>
                {canManage && member.email !== user?.email && (
                  <button
                    onClick={() => removeMember.mutate(member.user_id)}
                    disabled={removeMember.isPending}
                    className="p-1.5 text-content-muted hover:text-danger rounded transition-colors disabled:opacity-40"
                    title="Quitar del proyecto"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Dialog>
  );
}
